/**
 * How wide the screen's content may be: `normal`, centred under a limit that
 * keeps text lines readable, or `wide`, using every pixel — for a board of
 * columns on an ultra-wide monitor.
 *
 * One choice for the whole application, made with the header button and
 * remembered in `localStorage` like the colour mode. A screen does not choose
 * for itself: the reader knows their monitor, the screen does not.
 */
import { useCallback, useState } from 'react';

export type ContentWidth = 'normal' | 'wide';

/** The `normal` limit, in pixels. */
export const NORMAL_CONTENT_WIDTH = 1400;

const STORAGE_KEY = 'forsys.contentWidth';

function readStored(): ContentWidth {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'wide' ? 'wide' : 'normal';
  } catch {
    // Private browsing, or storage disabled. The normal width is a fine default.
    return 'normal';
  }
}

export function useContentWidth(): { width: ContentWidth; toggle: () => void } {
  const [width, setWidth] = useState<ContentWidth>(readStored);

  const toggle = useCallback(() => {
    setWidth((current) => {
      const next: ContentWidth = current === 'wide' ? 'normal' : 'wide';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Not being able to remember the choice must not stop it taking effect.
      }
      return next;
    });
  }, []);

  return { width, toggle };
}
